import React, { useEffect, useState } from 'react';

import {
	Chart,
	ChartControlPanel,
	DateBreadcrumbs,
	ExportActionsTypes,
	FileTypeSelectionCommonForm,
	Modal,
	SpinnerOverlay } from '@components/common';

import { kpi } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { checkIsMultiDays, getDateByTimeAggregationType } from '@utils/kpi-utils/date';
import { prepareTieInChartToExport } from '@utils/kpi-utils/prepareChartData';

import { IExportKpiCompareDTO, IKpiChartsDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IChart } from '@interfaces/kpi/kpiCommon';
import { ICompareChartsCardTypes, TDetailedDrillTime } from './CompareChartsCard.types';

import {
	ETimeAggregationTypes,
	EVendorsValues,
	TExportFileTypes,
} from '@enums/common';

import { exportRadioButtonsData, timezoneUTC } from '@constants/common';
import { listOfTimeAggregationTypes } from '@constants/KPI/kpi-common';
import { MODAL_WIDTH_455 } from '@constants/modals';

import styles from './CompareChartsCard.module.scss';

const CompareChartsCard = ({
	compareCharts,
	compareSettings,
	compareDrillDownCharts,
	getDrillDownCompareCharts,
	isLoadingDrillDownCompareCharts,
	exportCompareCharts,
}: ICompareChartsCardTypes) => {
	const isOranVendor = compareCharts.some(chart => chart.vendor.name === EVendorsValues.oran);
	const isMultiDays = checkIsMultiDays({
		points: compareCharts[0].lines[0].points,
		organizationTimezone: timezoneUTC,
	});
	const { timeAggregationType } = compareSettings;

	const { chartsReceivedAt } = useAppSelector(kpi);

	const [isAxisVisible, setIsAxisVisible] = useState(false);
	const [isLogarithmic, setIsLogarithmic] = useState(false);

	const toggleLogarithmic = () => setIsLogarithmic(prevState => !prevState);

	const toggleAxisVisibility = () => setIsAxisVisible(prevState => !prevState);

	const defaultIndexTimeAggregationType = timeAggregationType
		? listOfTimeAggregationTypes.indexOf(timeAggregationType)
		: 0;

	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);
	const [detailedDrillTime, setDetailedDrillTime] = useState<TDetailedDrillTime | unknown>({});
	const [currentIndexTimeAggregationType, setCurrentIndexTimeAggregationType]
		= useState<number>(defaultIndexTimeAggregationType);
	const [currentCharts, setCurrentCharts] = useState<Array<IChart>>(compareCharts);

	useEffect(() => {
		if (!compareDrillDownCharts.length
			|| defaultIndexTimeAggregationType === currentIndexTimeAggregationType) return;

		setCurrentCharts(compareDrillDownCharts);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [compareDrillDownCharts]);

	const drillDown = (dates: Array<number>) => {
		if (isOranVendor) {
			return;
		}

		if (timeAggregationType === ETimeAggregationTypes.months
			|| currentIndexTimeAggregationType - 1 < 0) return;

		const selectedTimeAggregationType = listOfTimeAggregationTypes[currentIndexTimeAggregationType - 1];

		const drillDownData: Array<IKpiChartsDTO> = dates.map((date, index) => {
			const start = getDateByTimeAggregationType({
				date,
				timeAggregationType: selectedTimeAggregationType,
				isStartOfDate: true,
			});

			const end = getDateByTimeAggregationType({
				date,
				timeAggregationType: selectedTimeAggregationType,
				isStartOfDate: false,
			});

			return ({
				from: start,
				to: end,
				aggregationType: currentCharts[index].aggregationType.name,
				kpiIds: [currentCharts[index].kpiId],
				sites: currentCharts[index].sites,
				timeAggregationType: selectedTimeAggregationType,
				vendor: currentCharts[index].vendor.name,
			});
		});

		getDrillDownCompareCharts(drillDownData);

		setCurrentIndexTimeAggregationType(currentIndexTimeAggregationType - 1);

		setDetailedDrillTime((prevState: TDetailedDrillTime) => ({
			...prevState,
			[selectedTimeAggregationType]: drillDownData.map(chartData => ({
				from: chartData.from,
				to: chartData.to,
			})),
		}));
	};

	const drillUp = () => {
		const selectedTimeAggregationType = listOfTimeAggregationTypes[currentIndexTimeAggregationType + 1];

		if (defaultIndexTimeAggregationType === currentIndexTimeAggregationType + 1) {
			setCurrentCharts(compareCharts);
			setCurrentIndexTimeAggregationType(currentIndexTimeAggregationType + 1);
			setDetailedDrillTime({});
		} else if (Object.keys(detailedDrillTime as TDetailedDrillTime).includes(selectedTimeAggregationType)) {
			const prevTime: Array<{
				from: number;
				to: number;
			}> = (detailedDrillTime as any)[selectedTimeAggregationType];

			const drillUpData: Array<IKpiChartsDTO> = prevTime.map((dates, index) => ({
				from: dates.from,
				to: dates.to,
				aggregationType: currentCharts[index].aggregationType.name,
				kpiIds: [currentCharts[index].kpiId],
				sites: currentCharts[index].sites,
				timeAggregationType: listOfTimeAggregationTypes[currentIndexTimeAggregationType + 1],
				vendor: currentCharts[index].vendor.name,
			}));

			getDrillDownCompareCharts(drillUpData);

			setCurrentIndexTimeAggregationType(currentIndexTimeAggregationType + 1);
		}
	};

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const closeTypeSelectionForm = () => setIsOpenFileTypeSelectionModal(false);

	const exportHandler = (data: { fileType: TExportFileTypes }) => {
		if (!chartsReceivedAt) return;

		const exportData: Array<IExportKpiCompareDTO> = currentCharts.map(chart => ({
			...prepareTieInChartToExport(chart),
			fileType: data.fileType,
			lastSeenTime: chartsReceivedAt,
		}));

		exportCompareCharts(exportData);
	};

	return (
		<div className={styles.compareChartsCardContainer}>
			{isLoadingDrillDownCompareCharts && <SpinnerOverlay />}
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionCommonForm
					activeExportAction={ExportActionsTypes.all}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={exportHandler}
					isLoading={false}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			<DateBreadcrumbs
				currentIndexTimeAggregationType={currentIndexTimeAggregationType}
				defaultIndexTimeAggregationType={defaultIndexTimeAggregationType}
				isMultiDays={isMultiDays}
				isOranVendor={isOranVendor}
			/>
			<div className={styles.compareChartsCardChart}>
				<ChartControlPanel
					charts={compareCharts}
					downloadChart={toggleFileTypeSelectionModal}
					drillUp={drillUp}
					isAxisVisible={isAxisVisible}
					isBackIcon={defaultIndexTimeAggregationType !== currentIndexTimeAggregationType}
					isCheckedLogarithmic={isLogarithmic}
					isCompare
					isFullCard
					prevTimeAggregationType={listOfTimeAggregationTypes[currentIndexTimeAggregationType + 1]}
					toggleAxisVisibility={toggleAxisVisibility}
					toggleLogarithmic={toggleLogarithmic}
				/>

				<Chart
					charts={currentCharts}
					fitWindowHeight
					isAxisVisible={isAxisVisible}
					isLogarithmic={isLogarithmic}
					isMaintainAspectRatio={false}
					legendPlacement='top'
					onClick={drillDown}
					showAxesTitles
				/>
			</div>
		</div>
	);
};

export default CompareChartsCard;
